import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CloneSignatureMatrixTemplates, DeleteSignatureMatrixTemplate, UpdateSignatureMatrixTemplate } from '@completion/actions';
import { SignatureType } from '@completion/enums';
import { Confirmation, Discipline, Project, SignatureMatrixTemplate } from '@completion/models';
import { getCurrentProject } from '@completion/selectors';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog';
import { MatrixEditDialogComponent } from '../matrix-template-edit-dialog/matrix-template-edit-dialog.component';
import {
  DisciplineSelectDialogComponent,
  DisciplineSelectDialogData,
} from '../discipline-select-dialog/discipline-select-dialog.component';

export interface DisciplineGroup {
  discipline: Discipline | null;
  label: string;
  templates: SignatureMatrixTemplate[];
}

@Component({
  selector: 'app-signature-matrix-template-list',
  templateUrl: './signature-matrix-template-list.component.html',
  styleUrls: ['./signature-matrix-template-list.component.scss'],
  standalone: false,
})
export class SignatureMatrixTemplateListComponent implements OnInit, OnDestroy {
  allTemplatesForType: Array<SignatureMatrixTemplate> = [];
  disciplineGroups: DisciplineGroup[] = [];
  type: SignatureType;
  isCustomType: boolean;
  customMatrixType: string;
  needsDefaultTemplate = false;

  @Input()
  set signatureType(type: string) {
    this.type = SignatureType[type];
  }
  @Input()
  set customType(customtype: boolean) {
    this.isCustomType = customtype;
  }
  @Input()
  set customTypeName(customType: string) {
    this.customMatrixType = customType;
  }

  displayedColumns = ['ordinal', 'verified', 'role', 'signee', 'signatureRole', 'edit', 'delete'];

  @Input() idx: number;

  private readonly destroy$ = new Subject<void>();
  private currentProject: Project;

  constructor(
    private readonly store: Store,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project) {
          this.currentProject = project;
          const matrices = project.signatureMatrixTemplates;

          if (matrices !== null && matrices[this.type]) {
            let templates: SignatureMatrixTemplate[];
            if (this.isCustomType) {
              templates = matrices[this.type].filter((matrix) => {
                return matrix.projectDefinedType === this.customMatrixType;
              });
            } else {
              templates = matrices[this.type];
            }

            this.allTemplatesForType = templates;
            this.buildDisciplineGroups(templates);
          } else {
            this.allTemplatesForType = [];
            this.disciplineGroups = [];
            this.needsDefaultTemplate = false;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildDisciplineGroups(templates: SignatureMatrixTemplate[]): void {
    const defaultTemplates = templates.filter((t) => !t.discipline);
    const disciplineTemplates = templates.filter((t) => !!t.discipline);

    // Build groups: default first, then one per discipline
    const groups: DisciplineGroup[] = [];

    groups.push({
      discipline: null,
      label: 'Default',
      templates: defaultTemplates,
    });

    const disciplineMap = new Map<number, { discipline: Discipline; templates: SignatureMatrixTemplate[] }>();
    for (const t of disciplineTemplates) {
      const existing = disciplineMap.get(t.discipline.id);
      if (existing) {
        existing.templates.push(t);
      } else {
        disciplineMap.set(t.discipline.id, { discipline: t.discipline, templates: [t] });
      }
    }

    for (const [, entry] of disciplineMap) {
      groups.push({
        discipline: entry.discipline,
        label: entry.discipline.disciplineName
          ? `${entry.discipline.disciplineNumber} - ${entry.discipline.disciplineName}`
          : entry.discipline.disciplineNumber,
        templates: entry.templates,
      });
    }

    this.disciplineGroups = groups;
    this.needsDefaultTemplate = disciplineTemplates.length > 0 && defaultTemplates.length === 0;
  }

  isLoading() {
    return false;
  }

  getGroupRoleSummary(group: DisciplineGroup): string {
    const roles = group.templates.map((t) => t.signatureRole?.name || t.role).filter(Boolean);
    const unique = [...new Set(roles)];
    return unique.length > 3 ? unique.slice(0, 3).join(', ') + '…' : unique.join(', ');
  }

  editSignatureMatrix(signatureMatrixTemplate: SignatureMatrixTemplate) {
    const di = this.dialog.open(MatrixEditDialogComponent, {
      minWidth: '900px',
      data: {
        signatureMatrixTemplate: signatureMatrixTemplate,
      },
      width: '800px',
    });

    di.afterClosed().subscribe((result) => {
      if (result) {
        this.store.dispatch(new UpdateSignatureMatrixTemplate(signatureMatrixTemplate));
      }
    });
  }

  deleteSignatureMatrix(signatureMatrixTemplate: SignatureMatrixTemplate) {
    const di = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete Signature Matrix Template',
        message: 'Are you sure you want to delete this signature matrix template?',
        isCancelDisplay: true,
        confirmationTitle: 'Confirm',
      } as Confirmation,
    });

    di.afterClosed().subscribe((result) => {
      if (result) {
        this.store.dispatch(new DeleteSignatureMatrixTemplate(signatureMatrixTemplate));
      }
    });
  }

  addSignatureMatrixRow(group: DisciplineGroup) {
    const di = this.dialog.open(MatrixEditDialogComponent, {
      data: {
        signatureMatrixTemplate: {
          ordinal: group.templates.length + 1,
          role: null,
          verified: null,
          signee: null,
          signatureRole: null,
          signatureType: this.type,
          projectDefinedType: this.customMatrixType ? this.customMatrixType : null,
          discipline: group.discipline || null,
        } as SignatureMatrixTemplate,
      },
      width: '800px',
    });

    di.afterClosed().subscribe(() => {});
  }

  /**
   * Opens a discipline selector, then clones ALL rows from the given group
   * into the selected discipline.
   */
  cloneGroupToDiscipline(group: DisciplineGroup): void {
    const existingDisciplines = this.disciplineGroups.filter((g) => g.discipline !== null).map((g) => g.discipline);

    const di = this.dialog.open(DisciplineSelectDialogComponent, {
      width: '500px',
      data: {
        title: `Clone "${group.label}" template to Discipline`,
        existingDisciplines,
      } as DisciplineSelectDialogData,
    });

    di.afterClosed().subscribe((selectedDiscipline: Discipline | null) => {
      if (selectedDiscipline && group.templates.length > 0) {
        const cloned = group.templates.map(
          (template) =>
            ({
              ...template,
              id: undefined,
              discipline: selectedDiscipline,
            }) as SignatureMatrixTemplate,
        );

        this.store.dispatch(new CloneSignatureMatrixTemplates(cloned));
      }
    });
  }

  /**
   * Opens a discipline selector then opens the matrix edit dialog
   * to create the first row for a new discipline-specific template.
   */
  addForDiscipline(): void {
    const existingDisciplines = this.disciplineGroups.filter((g) => g.discipline !== null).map((g) => g.discipline);

    const di = this.dialog.open(DisciplineSelectDialogComponent, {
      width: '500px',
      data: {
        title: 'Add Template for Discipline',
        existingDisciplines,
      } as DisciplineSelectDialogData,
    });

    di.afterClosed().subscribe((selectedDiscipline: Discipline | null) => {
      if (selectedDiscipline) {
        this.dialog.open(MatrixEditDialogComponent, {
          minWidth: '900px',
          width: '800px',
          data: {
            signatureMatrixTemplate: {
              ordinal: 1,
              role: null,
              verified: null,
              signee: null,
              signatureRole: null,
              signatureType: this.type,
              projectDefinedType: this.customMatrixType ? this.customMatrixType : null,
              discipline: selectedDiscipline,
            } as SignatureMatrixTemplate,
          },
        });
      }
    });
  }

  convertSignatureTypeToName(type: SignatureType) {
    if (this.isCustomType) {
      return this.customMatrixType;
    }
    switch (type) {
      case SignatureType.CSA_CP:
        return 'CP Checksheet';
      case SignatureType.CSA_MCP:
        return 'MCP Checksheet';
      case SignatureType.COMPANY:
        return 'Company Checksheet';
      case SignatureType.CHECK_SHEET:
        return 'By Checksheet';
      case SignatureType.MCC:
        return 'Mc Certificate';
      case SignatureType.RFCC:
        return 'Rfc Certificate';
      case SignatureType.RFOC:
        return 'Rfo Certificate';
      case SignatureType.HANDOVER:
        return 'Handover';
    }
  }
}
